import { CommonModule, NgFor, NgIf } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { firstValueFrom } from 'rxjs';
import {
  Posto,
  SeletorPostosComponent,
} from 'src/app/rede-especial/seletor-postos/seletor-postos.component';
import { MatSpinnerOverlayComponent } from '../../components/mat-spinner-overlay/mat-spinner-overlay.component';
import { IFrotaModel } from '../../frota/model/frota.model';
import { LocalStorageService } from '../../services/localstorage.service';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { AcordosExistentesComponent } from '../acordos-existentes/acordos-existentes.component';
import { ConfirmarDescontoAcrescimoComponent } from '../confirmar-desconto-acrescimo/confirmar-desconto-acrescimo.component';
import { DialogResultadoAcordoComponent } from '../dialog-resultado-acordo/dialog-resultado-acordo.component';
import { FiltrarFrotaComponent } from '../filtrar-frota/filtrar-frota.component';
import { TipoAcordoEnum } from '../rede-especial-enuns';
import {
  AcordoModel,
  BuscarPrecosMacroPostosItem,
  CombustivelContrato,
  PayloadCriarAcordo,
  PrecosMacroPostosItem,
} from '../rede-especial.interface';
import { RedeEspecialService } from '../rede-especial.service';

// Constantes do componente
const CONSTANTES = {
  LARGURA_DIALOG_RESULTADO: '900px',
  TIPO_USUARIO_MACRO: 8,
  IND_COMBUSTIVEL_PADRAO: 'W',
  TIPOS_ACORDO: {
    PRECO_FIXO: '2',
    ACRESCIMO: '4',
    DESCONTO: '5',
  },

  CAMPOS_FORMULARIO: {
    BUSCA_FROTA: 'buscaFrota',
    COD_FROTA: 'codFrota',
    TIPO: 'tipo',
    COMBUSTIVEL: 'combustivel',
    VALOR_FIXO: 'valorFixo',
    VALOR_DESCONTO_EM_REAIS: 'valorDescontoEmReais',
  },
} as const;

@Component({
  selector: 'app-criar-novo-acordo',
  templateUrl: './criar-novo-acordo.component.html',
  styleUrls: ['./criar-novo-acordo.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgFor,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    CurrencyMaskModule,
    AcordosExistentesComponent,
    MatSpinnerOverlayComponent,
    SeletorPostosComponent,
    ConfirmarDescontoAcrescimoComponent,
  ],
})
export class CriarNovoAcordoComponent {
  public dialog = inject(MatDialog);
  public loading = false;
  public usuarioEhMacroPosto = false;
  public postosSelecionados: Posto[] = [];
  public frotaSelecionada!: IFrotaModel;
  public tipoAcordoEnum: TipoAcordoEnum = TipoAcordoEnum.PRECO_FIXO;
  public precosPostos!: BuscarPrecosMacroPostosItem[];
  public postosSemPreco!: Posto[];
  public precoFinalSugerido!: number;
  public codEntidadeMacro!: number;
  public precos: BuscarPrecosMacroPostosItem[] = [];

  public combustiveis: CombustivelContrato[] = [];

  private localStorageService = inject(LocalStorageService);
  private service = inject(RedeEspecialService);
  private snackBar = inject(SnackBarService);
  indCombustivel!: string;
  mostrarTelaDescontosAcrescimos: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CriarNovoAcordoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CombustivelContrato[],
  ) {
    this.combustiveis = data;
    this.inicializarDadosUsuario();
  }

  private inicializarDadosUsuario(): void {
    try {
      const userData = this.localStorageService.getItem('userData');
      const usuario = JSON.parse(userData);

      this.codEntidadeMacro = usuario.retorno.codigoEntidade;
      this.usuarioEhMacroPosto =
        usuario.retorno.tipo == CONSTANTES.TIPO_USUARIO_MACRO;
    } catch (error) {
      this.snackBar.flashMessage(
        'Erro ao recuperar dados do usuário. Verifique o console para mais detalhes.',
        ['snackbar-error'],
        'Fechar',
        2,
      );
    }
  }

  private getValorAbsoluto(valor: number | undefined): number {
    valor = valor ? parseFloat(valor.toFixed(2)) : 0;
    return Math.abs(valor || 0);
  }

  private getCampoFormulario<T>(campo: string): T | null {
    return (this.acordosForm.get(campo)?.value as T) ?? null;
  }

  private getTipoAcordoSelecionado(): TipoAcordoEnum {
    return parseInt(
      String(
        this.getCampoFormulario<string>(CONSTANTES.CAMPOS_FORMULARIO.TIPO),
      ),
      10,
    ) as TipoAcordoEnum;
  }

  private getCombustivelSelecionado(): CombustivelContrato {
    return this.getCampoFormulario<CombustivelContrato>(
      CONSTANTES.CAMPOS_FORMULARIO.COMBUSTIVEL,
    ) as CombustivelContrato;
  }

  private getCodFrotaSelecionada(): number {
    return Number(
      this.getCampoFormulario<number>(CONSTANTES.CAMPOS_FORMULARIO.COD_FROTA),
    );
  }

  private getValorDescontoEmReais(): number {
    return Number(
      this.getCampoFormulario<number>(
        CONSTANTES.CAMPOS_FORMULARIO.VALOR_DESCONTO_EM_REAIS,
      ) ?? 0,
    );
  }

  private getValorFixo(): number | null {
    const valorFixo = this.getCampoFormulario<string | number>(
      CONSTANTES.CAMPOS_FORMULARIO.VALOR_FIXO,
    );
    return valorFixo === '' || valorFixo == null
      ? null
      : parseFloat(String(valorFixo));
  }

  private getCodsPostosSelecionadosParaBusca(): string {
    const codsPostos = this.postosSelecionados.map((p) => p.codPosto).join(',');
    return codsPostos || this.codEntidadeMacro.toString();
  }

  async buscarPrecosPostosMacroPostos(): Promise<void> {
    const payload = {
      codsPostos: this.getCodsPostosSelecionadosParaBusca(),
      indCombustivel: this.indCombustivel,
    };

    const res = await firstValueFrom(
      this.service.getPrecosPostosMacroPostos(payload),
    );
    this.precos = res.retorno || [];
  }

  async buscarPrecoCombustivel($event: MatSelectChange) {
    this.indCombustivel = $event.value?.descricaoCombustivel;
    if (!this.usuarioEhMacroPosto) {
       const payload = {
      codsPostos: this.getCodsPostosSelecionadosParaBusca(),
      indCombustivel: this.indCombustivel,
    };

    const res = await firstValueFrom(
      this.service.getPrecosPostosMacroPostos(payload),
    );
    this.precos = res.retorno || [];
 }

  }

  validarDescontoAcrescimo() {
    const precoPosto = this.precos[0]?.precoUnitario || 0;
    this.acordosForm.get('valorDescontoEmReais')?.setErrors(null);

    if (this.getTipoAcordoSelecionado() === TipoAcordoEnum.DESCONTO &&  !this.usuarioEhMacroPosto) {
      // Preço não pode ser maior que o preço do posto
      if (this.getValorDescontoEmReais() > precoPosto) {
        this.acordosForm
          .get('valorDescontoEmReais')
          ?.setErrors({ invalid: true });
      }
    } else if (this.getTipoAcordoSelecionado() === TipoAcordoEnum.ACRESCIMO && !this.usuarioEhMacroPosto) {
      // Preço não pode ser menor que o preço do posto
      if (this.getValorDescontoEmReais() < precoPosto) {
        this.acordosForm
          .get('valorDescontoEmReais')
          ?.setErrors({ invalid: true });
      }
    }
  }

  async montarPayloadDescontosAcrescimoPostos(): Promise<PayloadCriarAcordo> {
    await this.buscarPrecosPostosMacroPostos();

    const codFrota = this.getCodFrotaSelecionada();
    const combustivel = this.getCombustivelSelecionado();
    const tipo = this.getTipoAcordoSelecionado();
    const indCombustivel = combustivel.descricaoCombustivel;
    const valorFixo = null;
    const valorDescontoEmReais = this.getValorDescontoEmReais();

    const payload: PayloadCriarAcordo = {
      codsPostos: this.mapearPostosComDescontoAcrescimo(
        tipo,
        valorDescontoEmReais,
      ),
      codFrota,
      combustivel: combustivel.codigoCombustivel,
      indCombustivel: indCombustivel || CONSTANTES.IND_COMBUSTIVEL_PADRAO,
      tipo,
      valorFixo,
      valorDescontoEmReais: valorDescontoEmReais,
    };

    return payload;
  }

  private mapearPostosComDescontoAcrescimo(
    tipo: TipoAcordoEnum,
    valorDescontoEmReais: number,
  ): PrecosMacroPostosItem[] {
    return this.precos.map((preco) => ({
      codPosto: preco.codPosto,
      descontoAcrescimo:
        tipo === TipoAcordoEnum.DESCONTO
          ? this.getValorAbsoluto(preco.precoUnitario - valorDescontoEmReais)
          : this.getValorAbsoluto(valorDescontoEmReais - preco.precoUnitario),
    }));
  }

  montarPayloadDescontosAcressimoMacroPostos(): PayloadCriarAcordo {
    const precoFinalSugerido = this.precos[0].precoFinal;

    const codFrota = this.getCodFrotaSelecionada();
    const combustivel = this.getCombustivelSelecionado();
    const tipo = this.getTipoAcordoSelecionado();
    const indCombustivel = combustivel.descricaoCombustivel;
    const valorFixo = null;
    const valorDescontoEmReais = this.getValorDescontoEmReais();

    const payload: PayloadCriarAcordo = {
      codsPostos: this.mapearPostosComDescontoAcrescimo(
        tipo,
        valorDescontoEmReais,
      ),
      codFrota,
      combustivel: combustivel.codigoCombustivel,
      indCombustivel: indCombustivel || CONSTANTES.IND_COMBUSTIVEL_PADRAO,
      tipo,
      valorFixo,
      valorDescontoEmReais: precoFinalSugerido || valorDescontoEmReais,
    };

    return payload;
  }

  onConfirmarDescontos(precos: BuscarPrecosMacroPostosItem[]): void {
    this.precos = precos;
    const payload = this.montarPayloadDescontosAcressimoMacroPostos();
    this.enviarPayloadAcordo(payload);
  }

  onCancelarConfirmacao() {
    this.mostrarTelaDescontosAcrescimos = false;
  }

  exibirBotoesAcaoInicial(): boolean {
    return !this.precosPostos ? true : this.precosPostos.length > 1;
  }

  onCancelar() {
    this.dialogRef.close(false);
  }
  verificarPostos() {
    return this.postosSelecionados.length > 0;
  }
  isFormValid(): boolean {
    return this.acordosForm.valid;
  }

  acordosForm: FormGroup = new FormGroup({
    buscaFrota: new FormControl('', [Validators.required]),
    codFrota: new FormControl('', [Validators.required]),
    tipo: new FormControl(this.tipoAcordoEnum, [Validators.required]),
    combustivel: new FormControl(null, [Validators.required]),
    valorFixo: new FormControl(null),
    valorDescontoEmReais: new FormControl(null),
  });
  acordosExistentes: AcordoModel[] = [];
  msgAcordosExistentes = '';

  onTipoChange(event: any): void {
    const tipoAcordo = event.value;
    this.limparCamposDeValor();

    switch (tipoAcordo) {
      case CONSTANTES.TIPOS_ACORDO.PRECO_FIXO:
        this.acordosForm.get('valorFixo')?.setValidators([Validators.required]);
        this.acordosForm.get('valorDescontoEmReais')?.setValue(null);
        this.tipoAcordoEnum = TipoAcordoEnum.PRECO_FIXO;
        break;

      case CONSTANTES.TIPOS_ACORDO.ACRESCIMO:
        this.acordosForm
          .get('valorDescontoEmReais')
          ?.setValidators([Validators.required]);
        this.acordosForm.get('valorFixo')?.setValue(null);
        this.tipoAcordoEnum = TipoAcordoEnum.ACRESCIMO;
        break;

      case CONSTANTES.TIPOS_ACORDO.DESCONTO:
        this.acordosForm
          .get('valorDescontoEmReais')
          ?.setValidators([Validators.required]);
        this.acordosForm.get('valorFixo')?.setValue(null);
        this.tipoAcordoEnum = TipoAcordoEnum.DESCONTO;
        break;
    }
    this.atualizarValidacoesCamposDeValor();
  }

  private limparCamposDeValor(): void {
    this.acordosForm.get('valorFixo')?.setValue(null);
    this.acordosForm.get('valorFixo')?.setValidators(null);
    this.acordosForm.get('valorDescontoEmReais')?.setValue(null);
    this.acordosForm.get('valorDescontoEmReais')?.setValidators(null);
  }

  private atualizarValidacoesCamposDeValor(): void {
    this.acordosForm.get('valorFixo')?.updateValueAndValidity();
    this.acordosForm.get('valorDescontoEmReais')?.updateValueAndValidity();
  }
  verificarCliqueLupa(): void {
    const buscaFrota = this.acordosForm.get('buscaFrota')?.value;
    const codFrota = this.acordosForm.get('codFrota')?.value;

    if (buscaFrota && buscaFrota !== '' && codFrota === '') {
      this.acordosForm.get('buscaFrota')?.setErrors({
        required: true,
      });
    }
  }
  limparDadosFrota(): void {
    this.acordosForm.get('buscaFrota')?.setValue('');
    this.acordosForm.get('codFrota')?.setValue('');
    this.frotaSelecionada = null as any;
  }

  private selecionarFrota(frota: IFrotaModel): void {
    this.frotaSelecionada = frota;
    this.acordosForm.get('codFrota')?.setValue(frota.codigo);
    this.acordosForm.get('buscaFrota')?.setErrors(null);
  }

  openDialog(): void {
    const buscaFrota = this.acordosForm.get('buscaFrota')?.value;

    if (buscaFrota) {
      const dialogRef = this.dialog.open(FiltrarFrotaComponent, {
        width: '90vw',
        maxWidth: '700px',
        maxHeight: '80vh',
        data: buscaFrota,
      });

      dialogRef.afterClosed().subscribe((frota: IFrotaModel | null) => {
        frota ? this.selecionarFrota(frota) : this.limparDadosFrota();
      });
    }
  }

  atualizarPostos(postos: Posto[]): void {
    this.postosSelecionados = postos;
  }

  private exibirDialogResultado(sucessos: string[], erros: string[]): void {
    this.dialog.open(DialogResultadoAcordoComponent, {
      width: CONSTANTES.LARGURA_DIALOG_RESULTADO,
      disableClose: true,
      autoFocus: false,
      restoreFocus: false,
      data: { sucessos, erros },
    });
  }

  private processarResultadoAcordo(resultados: any[]): {
    sucessos: string[];
    erros: string[];
  } {
    const sucessos: string[] = [];
    const erros: string[] = [];

    resultados.forEach((resultado) => {
      if (resultado.sucesso) {
        sucessos.push(resultado.msgErro);
      } else {
        erros.push(resultado.msgErro);
      }
    });
    return { sucessos, erros };
  }

  private finalizarProcessamento(): void {
    this.dialogRef.close();
    this.loading = false;
  }

  private enviarPayloadAcordo(payload: PayloadCriarAcordo): void {
    this.loading = true;

    this.service.criarNovoAcordo(payload).subscribe({
      next: (res) => {
        const { sucessos, erros } = this.processarResultadoAcordo(res.retorno);
        this.exibirDialogResultado(sucessos, erros);
        this.finalizarProcessamento();
      },
      error: () => {
        this.exibirDialogResultado([], ['Erro ao criar acordo']);
        this.finalizarProcessamento();
      },
    });
  }

  montarPayloadAcordoPrecoFixo() {
    const combustivel = this.getCombustivelSelecionado();
    const codCombustivel = combustivel.codigoCombustivel;
    const indCombustivel = combustivel?.descricaoCombustivel;
    const codFrota = this.getCodFrotaSelecionada();
    const valorFixo = this.getValorFixo();
    const tipo = this.getTipoAcordoSelecionado();

    const codsPostos = this.usuarioEhMacroPosto
      ? this.postosSelecionados.map((p) => ({
          codPosto: p.codPosto,
          descontoAcrescimo: 0,
        }))
      : [{ codPosto: this.codEntidadeMacro, descontoAcrescimo: 0 }];

    const payload: PayloadCriarAcordo = {
      codsPostos,
      codFrota,
      combustivel: codCombustivel,
      indCombustivel: indCombustivel || CONSTANTES.IND_COMBUSTIVEL_PADRAO,
      tipo,
      valorFixo,
      valorDescontoEmReais: null, // Para preço fixo, o desconto em reais é sempre null
    };
    return payload;
  }

  async criarAcordosPrecosFixos(): Promise<void> {
    if (!this.acordosForm.valid) {
      this.snackBar.flashMessage(
        'Preencha todos os campos obrigatórios.',
        ['snackbar-error'],
        'Fechar',
        2,
      );
      return;
    }

    const tipoAcordo = this.getTipoAcordoSelecionado();
    if (tipoAcordo === TipoAcordoEnum.PRECO_FIXO) {
      const payload = this.montarPayloadAcordoPrecoFixo();
      this.enviarPayloadAcordo(payload);
    } else {
      const payload = await this.montarPayloadDescontosAcrescimoPostos();
      this.enviarPayloadAcordo(payload);
    }
  }

  onConfirmarDescontosAcrescimos() {
    this.mostrarTelaDescontosAcrescimos = true;
  }

  necessarioConfirmarDescontoAcrescimo(): boolean {
    const tipo = parseInt(this.acordosForm.get('tipo')?.value);

    const retorno =
      this.usuarioEhMacroPosto &&
      (tipo === TipoAcordoEnum.ACRESCIMO || tipo === TipoAcordoEnum.DESCONTO) &&
      this.acordosForm.valid;

    return retorno;
  }

  onPrecoFinalSugeridoAlterado(novoPreco: number) {
    this.precoFinalSugerido = novoPreco;
  }

  onRemoverPostoDaLista($event: number) {
    this.postosSelecionados = this.postosSelecionados.filter(
      (posto) => posto.codPosto !== $event,
    );

    if (this.postosSelecionados.length === 0) {
      this.precosPostos = [];
    }
  }
}
